/**
 * SpecialistLabProfile.jsx — iter659
 *
 * Displays parsed DUTCH + DNA data inside the OBSERVE phase of BHI.
 *
 *  • 🧪 Estrogen Detoxification Profile — 2-OH/16-OH ratio, methylation %,
 *       4-OH metabolite, cortisol pattern.
 *  • 🧬 Genetic Modulators Profile — MTHFR, COMT, GST, NQO1, BRCA SNPs +
 *       AI-derived actionable recommendations from /api/bhi/specialist-lab/profile.
 *
 * Educational only. No diagnostic claims. Strictly Bulgarian copy by default.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  FlaskConical, Dna, Loader2, Upload, AlertCircle, ChevronDown, ChevronUp,
  RefreshCcw, Sparkles, Activity,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import SpecialistLabUpload from "@/components/breast_intel/SpecialistLabUpload";
import ManualDutchEntryModal from "@/components/breast_intel/ManualDutchEntryModal";  // iter790 — manual entry
import DetoxStoryCard from "@/components/breast_intel/DetoxStoryCard";

const TX = {
  bg: {
    title_dutch: "🧪 Профил на естрогенната детоксикация (DUTCH)",
    title_dna:   "🧬 Профил на генетичните модулатори (ДНК)",
    title_horm:  "🩸 Серумни хормони (от лабораторни изследвания)",
    sub_dutch:   "Вашият метаболитен хормонален пейзаж — естрогенен път и метилиране",
    sub_dna:     "SNP варианти, влияещи върху метаболизма на естрогена и Phase II конюгацията",
    sub_horm:    "Латест стойности от качените серумни тестове + фаза на цикъла (когато е изводима)",
    empty_title: "Все още няма качени специализирани лаборатории",
    empty_desc:  "Качете DUTCH хормонален тест или генетичен (ДНК) панел — AI ще извлече структурираните маркери и ще ги визуализира тук.",
    upload:      "Качи DUTCH / ДНК тест",
    refresh:     "Обнови",
    confidence:  "Сигурност на извличането",
    no_dutch:    "Все още няма DUTCH данни",
    no_dna:      "Все още няма ДНК данни",
    uploaded:    "Качен на",
    expand:      "Покажи подробно",
    collapse:    "Скрий",
    estrogen_h:  "Естрогенни метаболити",
    androgens_h: "Андрогени",
    cortisol_h:  "Кортизолов профил",
    organic_h:   "Маркери на органични киселини",
    snps_h:      "SNP варианти",
    actionable:  "Какво означава това за Вас",
    no_flags:    "Не са открити неблагоприятни маркери — поддържайте текущия режим.",
    e2:          "Естрадиол (E2)",
    e1:          "Естрон (E1)",
    e3:          "Естриол (E3)",
    oh2:         "2-OH-E1",
    oh16:        "16-OH-E1",
    oh4:         "4-OH-E1",
    meo2:        "2-MeO-E1",
    ratio:       "2-OH / 16-OH съотношение",
    methyl:      "Ефективност на метилирането",
    dhea:        "DHEA-S",
    tt:          "Тестостерон",
    pattern:     "Шаблон",
    cortisol_d:  "Дневен ритъм",
    cortisol_t:  "Общ кортизол",
    cortisol_r:  "Cortisol/Cortisone съотношение",
    cortisol_m:  "Метаболизиран кортизол",
    gene:        "Ген",
    rsid:        "rsID",
    genotype:    "Генотип",
    impact:      "Ефект",
    parser_v:    "Parser",
    privacy:     "Данните са Ваши. Можете да изтриете по всяко време от профила.",
  },
  en: {
    title_dutch: "🧪 Estrogen Detoxification Profile (DUTCH)",
    title_dna:   "🧬 Genetic Modulators Profile (DNA)",
    title_horm:  "🩸 Serum Hormones (from lab tests)",
    sub_dutch:   "Your metabolic hormone landscape — estrogen pathway & methylation",
    sub_dna:     "SNP variants influencing estrogen metabolism and Phase II conjugation",
    sub_horm:    "Latest values from uploaded serum tests + cycle phase hint (when derivable)",
    empty_title: "No specialist labs uploaded yet",
    empty_desc:  "Upload a DUTCH hormone test or genomic (DNA) panel — AI will extract structured markers and display them here.",
    upload:      "Upload DUTCH / DNA",
    refresh:     "Refresh",
    confidence:  "Extraction confidence",
    no_dutch:    "No DUTCH data yet",
    no_dna:      "No DNA data yet",
    uploaded:    "Uploaded on",
    expand:      "Show details",
    collapse:    "Hide",
    estrogen_h:  "Estrogen metabolites",
    androgens_h: "Androgens",
    cortisol_h:  "Cortisol profile",
    organic_h:   "Organic-acid markers",
    snps_h:      "SNP variants",
    actionable:  "What this means for you",
    no_flags:    "No unfavourable markers detected — keep current regimen.",
    e2:          "Estradiol (E2)",
    e1:          "Estrone (E1)",
    e3:          "Estriol (E3)",
    oh2:         "2-OH-E1",
    oh16:        "16-OH-E1",
    oh4:         "4-OH-E1",
    meo2:        "2-MeO-E1",
    ratio:       "2-OH / 16-OH ratio",
    methyl:      "Methylation efficiency",
    dhea:        "DHEA-S",
    tt:          "Testosterone",
    pattern:     "Pattern",
    cortisol_d:  "Diurnal pattern",
    cortisol_t:  "Total cortisol",
    cortisol_r:  "Cortisol/Cortisone ratio",
    cortisol_m:  "Metabolised cortisol",
    gene:        "Gene",
    rsid:        "rsID",
    genotype:    "Genotype",
    impact:      "Impact",
    parser_v:    "Parser",
    privacy:     "Your data is yours. Delete any time from profile.",
  },
  es: {
    title_dutch: "🧪 Perfil de detoxificación de estrógenos (DUTCH)",
    title_dna:   "🧬 Perfil de moduladores genéticos (ADN)",
    title_horm:  "🩸 Hormonas séricas (de análisis)",
    sub_dutch:   "Su paisaje hormonal — vía del estrógeno y metilación",
    sub_dna:     "Variantes SNP que influyen en el metabolismo del estrógeno",
    sub_horm:    "Últimos valores de sus análisis séricos + fase del ciclo (cuando es deducible)",
    empty_title: "Sin laboratorios especializados aún",
    empty_desc:  "Suba un test DUTCH o un panel genómico — la IA extraerá los marcadores.",
    upload:      "Subir DUTCH / ADN",
    refresh:     "Actualizar",
    confidence:  "Confianza de extracción",
    no_dutch:    "Sin datos DUTCH",
    no_dna:      "Sin datos de ADN",
    uploaded:    "Subido el",
    expand:      "Mostrar detalle",
    collapse:    "Ocultar",
    estrogen_h:  "Metabolitos del estrógeno",
    androgens_h: "Andrógenos",
    cortisol_h:  "Perfil de cortisol",
    organic_h:   "Marcadores de ácidos orgánicos",
    snps_h:      "Variantes SNP",
    actionable:  "Qué significa para usted",
    no_flags:    "Sin marcadores desfavorables — mantenga el régimen actual.",
    e2:          "Estradiol (E2)",
    e1:          "Estrona (E1)",
    e3:          "Estriol (E3)",
    oh2:         "2-OH-E1",
    oh16:        "16-OH-E1",
    oh4:         "4-OH-E1",
    meo2:        "2-MeO-E1",
    ratio:       "Cociente 2-OH / 16-OH",
    methyl:      "Eficiencia de metilación",
    dhea:        "DHEA-S",
    tt:          "Testosterona",
    pattern:     "Patrón",
    cortisol_d:  "Ritmo diurno",
    cortisol_t:  "Cortisol total",
    cortisol_r:  "Cortisol/Cortisona",
    cortisol_m:  "Cortisol metabolizado",
    gene:        "Gen",
    rsid:        "rsID",
    genotype:    "Genotipo",
    impact:      "Impacto",
    parser_v:    "Parser",
    privacy:     "Sus datos son suyos. Elimínelos cuando desee.",
  },
};

const CONF_PILL = {
  high: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  mid:  "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  low:  "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300",
};

const IMPACT_PILL = {
  slow:           "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  reduced:        "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  low:            "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  absent:         "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  null_deletion:  "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  pathogenic:     "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  VUS:            "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300",
  increased:      "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  fast:           "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  intermediate:   "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300",
  wild_type:      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
};

function PillRow({ label, value, isDark, testid }) {
  if (value === null || value === undefined) return null;
  const v = typeof value === "number" ? value.toFixed(2) : value;
  return (
    <div className="flex items-center justify-between text-xs py-1" data-testid={testid}>
      <span className={isDark ? "text-gray-400" : "text-gray-600"}>{label}</span>
      <span className="font-bold tabular-nums">{v}</span>
    </div>
  );
}

function Card({ title, children, isDark, testid }) {
  return (
    <div
      className={`p-4 rounded-xl border ${isDark ? "bg-white/[0.02] border-white/10" : "bg-gray-50/60 border-gray-200"}`}
      data-testid={testid}
    >
      <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${isDark ? "text-purple-300" : "text-purple-700"}`}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ActionableFlags({ flags, t, isDark }) {
  const recs = Object.entries(flags || {}).filter(([k]) => k.endsWith("_recommendation"));
  if (recs.length === 0) {
    return (
      <div className={`p-3 rounded-xl text-xs italic ${isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`} data-testid="speclab-no-flags">
        {t.no_flags}
      </div>
    );
  }
  return (
    <div className={`p-3 rounded-xl border ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-300 bg-amber-50"}`} data-testid="speclab-flags">
      <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
        <Sparkles className="w-3.5 h-3.5" /> {t.actionable}
      </div>
      <ul className="space-y-2 text-xs">
        {recs.map(([k, v]) => (
          <li key={k} className="flex items-start gap-2">
            <span className={`mt-0.5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`}>▸</span>
            <span>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DutchPanel({ dutch, t, isDark }) {
  if (!dutch?.data) {
    return <div className="text-xs italic opacity-60" data-testid="speclab-no-dutch">{t.no_dutch}</div>;
  }
  const d = dutch.data;
  const e = d.estrogens || {};
  const a = d.androgens || {};
  const c = d.cortisol || {};
  const o = d.organic_acids || {};
  const conf = d.extraction_confidence || "mid";
  return (
    <div className="space-y-3" data-testid="speclab-dutch-panel">
      <div className="flex items-center justify-between text-[11px]">
        <span className="opacity-60">{t.uploaded} {dutch.uploaded_at ? new Date(dutch.uploaded_at).toLocaleDateString() : "—"}</span>
        <span className={`px-2 py-0.5 rounded-full font-bold ${CONF_PILL[conf] || CONF_PILL.mid}`}>{t.confidence}: {conf}</span>
      </div>

      <ActionableFlags flags={dutch.flags} t={t} isDark={isDark} />

      <div className="grid sm:grid-cols-2 gap-3">
        <Card title={t.estrogen_h} isDark={isDark} testid="dutch-estrogens">
          <PillRow label={t.e2} value={e.estradiol_e2_ng_mg} isDark={isDark} />
          <PillRow label={t.e1} value={e.estrone_e1_ng_mg}    isDark={isDark} />
          <PillRow label={t.e3} value={e.estriol_e3_ng_mg}    isDark={isDark} />
          <div className="my-1 border-t border-dashed border-gray-300/40" />
          <PillRow label={t.oh2}    value={e["2_oh_E1_ng_mg"]}   isDark={isDark} />
          <PillRow label={t.oh16}   value={e["16_oh_E1_ng_mg"]}  isDark={isDark} />
          <PillRow label={t.oh4}    value={e["4_oh_E1_ng_mg"]}   isDark={isDark} />
          <PillRow label={t.meo2}   value={e["2_meo_E1_ng_mg"]}  isDark={isDark} />
          <div className="my-1 border-t border-dashed border-gray-300/40" />
          <PillRow label={t.ratio}  value={e.ratio_2_OH_to_16_OH} isDark={isDark} testid="dutch-ratio" />
          <PillRow label={t.methyl} value={e.methylation_efficiency_pct ? e.methylation_efficiency_pct + "%" : null} isDark={isDark} />
        </Card>

        <div className="space-y-3">
          <Card title={t.androgens_h} isDark={isDark} testid="dutch-androgens">
            <PillRow label={t.dhea} value={a.dhea_s_ng_mg}        isDark={isDark} />
            <PillRow label={t.tt}   value={a.testosterone_ng_mg}  isDark={isDark} />
            <PillRow label={t.pattern} value={a.androgen_metabolism_pattern} isDark={isDark} />
          </Card>
          <Card title={t.cortisol_h} isDark={isDark} testid="dutch-cortisol">
            <PillRow label={t.cortisol_d} value={c.diurnal_pattern} isDark={isDark} />
            <PillRow label={t.cortisol_t} value={c.total_cortisol_ng_mg} isDark={isDark} />
            <PillRow label={t.cortisol_r} value={c.cortisol_to_cortisone_ratio} isDark={isDark} />
            <PillRow label={t.cortisol_m} value={c.metabolised_cortisol_pattern} isDark={isDark} />
          </Card>
          {(o.b6_marker || o.b12_marker || o.glutathione_marker) && (
            <Card title={t.organic_h} isDark={isDark} testid="dutch-organic">
              <PillRow label="B6"          value={o.b6_marker}          isDark={isDark} />
              <PillRow label="B12"         value={o.b12_marker}         isDark={isDark} />
              <PillRow label="Glutathione" value={o.glutathione_marker} isDark={isDark} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const HORMONE_LABELS_BG = {
  estradiol_e2_pmol_l:       { lab: "Естрадиол (E2)", unit: "pmol/L" },
  progesterone_nmol_l:       { lab: "Прогестерон",    unit: "nmol/L" },
  testosterone_total_nmol_l: { lab: "Тестостерон общ", unit: "nmol/L" },
  testosterone_free_pmol_l:  { lab: "Тестостерон св.", unit: "pmol/L" },
  shbg_nmol_l:               { lab: "SHBG",           unit: "nmol/L" },
  dhea_s_umol_l:             { lab: "DHEA-S",         unit: "µmol/L" },
  amh_pmol_l:                { lab: "AMH",            unit: "pmol/L" },
  fsh_iu_l:                  { lab: "FSH",            unit: "IU/L" },
  lh_iu_l:                   { lab: "LH",             unit: "IU/L" },
  prolactin_miu_l:           { lab: "Пролактин",      unit: "mIU/L" },
  cortisol_morning_nmol_l:   { lab: "Кортизол сутр.", unit: "nmol/L" },
  tsh_miu_l:                 { lab: "TSH",            unit: "mIU/L" },
  ft4_pmol_l:                { lab: "FT4",            unit: "pmol/L" },
  ft3_pmol_l:                { lab: "FT3",            unit: "pmol/L" },
};

const PHASE_LABEL_BG = {
  luteal:                        "🌙 Лутеална фаза",
  late_follicular_ovulatory:     "✨ Късна фоликуларна / овулация",
  early_follicular_or_menstrual: "💧 Ранна фоликуларна / менструация",
  postmenopausal:                "🌅 Постменопауза",
};

function SerumHormonesPanel({ trends, isDark }) {
  if (!trends || !trends.latest || Object.keys(trends.latest).length === 0) {
    return (
      <div className="text-xs italic opacity-60" data-testid="speclab-no-hormones">
        Все още няма качени серумни хормонални тестове. Качете лаборатория от {" "}
        <span className="font-semibold">Моите данни → Лабораторни изследвания и хормони</span>.
      </div>
    );
  }
  const latest = trends.latest;
  const entries = Object.entries(latest).filter(([k]) => HORMONE_LABELS_BG[k]);

  return (
    <div className="space-y-3" data-testid="speclab-hormones-panel">
      {trends.cycle_phase_hint && (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
          isDark ? "bg-pink-500/15 text-pink-300" : "bg-pink-100 text-pink-700"
        }`} data-testid="hormones-cycle-phase">
          {PHASE_LABEL_BG[trends.cycle_phase_hint] || trends.cycle_phase_hint}
        </div>
      )}

      <div className="text-[11px] opacity-60">
        {trends.total_uploads} качване{trends.total_uploads === 1 ? "" : "ия"} в историята
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {entries.map(([k, v]) => {
          const meta = HORMONE_LABELS_BG[k];
          return (
            <div
              key={k}
              className={`p-3 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50/60"}`}
              data-testid={`hormone-${k}`}
            >
              <div className={`text-[10px] uppercase tracking-wide font-bold mb-0.5 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                {meta.lab}
              </div>
              <div className="text-lg font-extrabold tabular-nums">
                {v.value} <span className="text-[10px] opacity-60 font-normal">{meta.unit}</span>
              </div>
              <div className="text-[10px] opacity-50 mt-0.5">{v.date}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GenomicPanel({ genomic, t, isDark }) {
  if (!genomic?.data) {
    return <div className="text-xs italic opacity-60" data-testid="speclab-no-dna">{t.no_dna}</div>;
  }
  const g = genomic.data;
  const snps = Array.isArray(g.snps) ? g.snps : [];
  const conf = g.extraction_confidence || "mid";
  return (
    <div className="space-y-3" data-testid="speclab-genomic-panel">
      <div className="flex items-center justify-between text-[11px]">
        <span className="opacity-60">{t.uploaded} {genomic.uploaded_at ? new Date(genomic.uploaded_at).toLocaleDateString() : "—"}</span>
        <span className={`px-2 py-0.5 rounded-full font-bold ${CONF_PILL[conf] || CONF_PILL.mid}`}>{t.confidence}: {conf}</span>
      </div>

      <ActionableFlags flags={genomic.flags} t={t} isDark={isDark} />

      <Card title={t.snps_h} isDark={isDark} testid="dna-snps">
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-xs">
            <thead>
              <tr className={isDark ? "text-gray-400" : "text-gray-500"}>
                <th className="text-left pb-1">{t.gene}</th>
                <th className="text-left pb-1">{t.rsid}</th>
                <th className="text-left pb-1">{t.genotype}</th>
                <th className="text-left pb-1">{t.impact}</th>
              </tr>
            </thead>
            <tbody>
              {snps.filter(s => s?.gene).map((s, i) => (
                <tr key={`${s.gene}-${s.rsid || i}`} className={`${isDark ? "border-t border-white/5" : "border-t border-gray-200"}`} data-testid={`dna-snp-${s.gene}`}>
                  <td className="py-1.5 font-bold">{s.gene}</td>
                  <td className="py-1.5 tabular-nums opacity-70">{s.rsid || "—"}</td>
                  <td className="py-1.5 tabular-nums">{s.genotype || "—"}</td>
                  <td className="py-1.5">
                    {s.impact ? (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${IMPACT_PILL[s.impact] || IMPACT_PILL.intermediate}`}>
                        {s.impact}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default function SpecialistLabProfile({ lang = "bg", refreshKey }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;

  const [profile, setProfile] = useState(null);
  const [hormoneTrends, setHormoneTrends] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualDutchOpen, setManualDutchOpen] = useState(false);   // iter790
  const [expanded, setExpanded] = useState({ dutch: true, dna: true, horm: true });

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [{ data: prof }, { data: tr }] = await Promise.all([
        axios.get(`${API}/bhi/specialist-lab/profile`),
        axios.get(`${API}/bhi/hormones/trends`).catch(() => ({ data: null })),
      ]);
      setProfile(prof);
      setHormoneTrends(tr);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const hasAny = profile && (
    profile.dutch
    || profile.genomic
    || (hormoneTrends && Object.keys(hormoneTrends.latest || {}).length > 0)
  );
  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"}`;

  if (loading && !profile) {
    return (
      <div className={`${cardCls} p-6 text-center`} data-testid="speclab-profile-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4 rounded-xl bg-red-500/10 text-red-500 text-sm flex items-start gap-2" data-testid="speclab-profile-error">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {err}
      </div>
    );
  }

  if (!hasAny) {
    return (
      <>
        <div className={`${cardCls} p-6 text-center`} data-testid="speclab-profile-empty">
          <div className="flex justify-center gap-2 mb-3 opacity-50">
            <FlaskConical className="w-7 h-7 text-purple-500" />
            <Dna className="w-7 h-7 text-pink-500" />
          </div>
          <h4 className="font-bold text-base mb-1">{t.empty_title}</h4>
          <p className={`text-xs max-w-md mx-auto mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t.empty_desc}</p>
          <button
            onClick={() => setUploadOpen(true)}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:opacity-90 inline-flex items-center gap-1.5"
            data-testid="speclab-profile-upload-cta"
          >
            <Upload className="w-3.5 h-3.5" /> {t.upload}
          </button>
          <button
            onClick={() => setManualDutchOpen(true)}
            className="ml-2 px-4 py-2 rounded-lg text-xs font-semibold bg-white ring-1 ring-purple-300 text-purple-700 hover:bg-purple-50 inline-flex items-center gap-1.5"
            data-testid="speclab-manual-dutch-cta"
          >
            <FlaskConical className="w-3.5 h-3.5" /> Ръчно въвеждане (без PDF)
          </button>
        </div>
        <SpecialistLabUpload
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          lang={lang}
          onParsed={() => load()}
        />
        <ManualDutchEntryModal
          open={manualDutchOpen}
          onClose={() => setManualDutchOpen(false)}
          onSaved={() => load()}
        />
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="speclab-profile">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => setUploadOpen(true)}
          className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:opacity-90 inline-flex items-center gap-1"
          data-testid="speclab-profile-upload-btn"
        >
          <Upload className="w-3 h-3" /> {t.upload}
        </button>
        <button
          onClick={() => setManualDutchOpen(true)}
          className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1 ${
            isDark ? "bg-purple-500/20 text-purple-200 ring-1 ring-purple-500/40 hover:bg-purple-500/30" : "bg-white ring-1 ring-purple-300 text-purple-700 hover:bg-purple-50"
          }`}
          data-testid="speclab-profile-manual-dutch-btn"
        >
          <FlaskConical className="w-3 h-3" /> Ръчно DUTCH
        </button>
        <button
          onClick={load}
          className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1 ${
            isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200"
          }`}
          data-testid="speclab-profile-refresh"
        >
          <RefreshCcw className="w-3 h-3" /> {t.refresh}
        </button>
      </div>

      {/* DUTCH */}
      <div className={`${cardCls} p-5`} data-testid="speclab-dutch-card">
        <button
          onClick={() => setExpanded((s) => ({ ...s, dutch: !s.dutch }))}
          className="w-full flex items-center justify-between gap-2 mb-2"
          data-testid="speclab-dutch-toggle"
        >
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-purple-500" />
            <div className="text-left">
              <h4 className="font-bold text-base">{t.title_dutch}</h4>
              <p className="text-[11px] opacity-60">{t.sub_dutch}</p>
            </div>
          </div>
          {expanded.dutch ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
        </button>
        {expanded.dutch && <DutchPanel dutch={profile.dutch} t={t} isDark={isDark} />}
      </div>

      {/* DNA */}
      <div className={`${cardCls} p-5`} data-testid="speclab-dna-card">
        <button
          onClick={() => setExpanded((s) => ({ ...s, dna: !s.dna }))}
          className="w-full flex items-center justify-between gap-2 mb-2"
          data-testid="speclab-dna-toggle"
        >
          <div className="flex items-center gap-2">
            <Dna className="w-5 h-5 text-pink-500" />
            <div className="text-left">
              <h4 className="font-bold text-base">{t.title_dna}</h4>
              <p className="text-[11px] opacity-60">{t.sub_dna}</p>
            </div>
          </div>
          {expanded.dna ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
        </button>
        {expanded.dna && <GenomicPanel genomic={profile.genomic} t={t} isDark={isDark} />}
      </div>

      {/* iter664 — Serum Hormones card (3rd panel) */}
      <div className={`${cardCls} p-5`} data-testid="speclab-hormones-card">
        <button
          onClick={() => setExpanded((s) => ({ ...s, horm: !s.horm }))}
          className="w-full flex items-center justify-between gap-2 mb-2"
          data-testid="speclab-hormones-toggle"
        >
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-rose-500" />
            <div className="text-left">
              <h4 className="font-bold text-base">{t.title_horm}</h4>
              <p className="text-[11px] opacity-60">{t.sub_horm}</p>
            </div>
          </div>
          {expanded.horm ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
        </button>
        {expanded.horm && <SerumHormonesPanel trends={hormoneTrends} isDark={isDark} />}
      </div>

      {/* iter660 — Phase 4: Detox Story AI explainer */}
      <DetoxStoryCard lang={lang} />

      <p className="text-[10px] opacity-50 italic text-center">{t.privacy}</p>

      <SpecialistLabUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lang={lang}
        onParsed={() => load()}
      />
      <ManualDutchEntryModal
        open={manualDutchOpen}
        onClose={() => setManualDutchOpen(false)}
        onSaved={() => load()}
      />
    </div>
  );
}
